/**
 * MigrationDetailPage — `/admin/migrations/:id`
 *
 * Single-migration operator view (ADR-102 §7):
 *   - Header with version bump, state, canary gate
 *   - 6 StatCard tiles (eligible/migrated/deferred/failed/manual_hold/pending)
 *   - Canary timeline (5 → 25 → 75 → 100)
 *   - Action panel (Pause/Resume · Proceed · Abort & Rollback)
 *   - Failed runs table with inline Retry/Rollback
 *   - Breaking changes card
 */
import {
  ArrowLeft,
  ArrowsClockwise,
  CaretRight,
  CheckCircle,
  Circle,
  PauseCircle,
  PlayCircle,
  Warning,
  XCircle,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Button } from '../../shared/ui/Button';
import { Card, CardBody, CardHeader } from '../../shared/ui/Card';
import { ProgressBar } from '../../shared/ui/ProgressBar';
import { StatCard } from '../../shared/ui/StatCard';
import { StatusBadge, type StatusVariant } from '../../shared/ui/StatusBadge';
import { cn } from '../../shared/utils/cn';
import {
  useApplyTenantRunAction,
  useMigrationRuns,
  useProfileMigration,
} from '../../features/profile-migrations/useProfileMigrations';
import type {
  BumpType,
  MigrationAction,
  MigrationRunRecord,
  MigrationState,
  ProfileMigrationRecord,
} from '../../features/profile-migrations/types';
import { MigrationActionDialog } from './MigrationActionDialog';

const STATE_VARIANT: Record<MigrationState, StatusVariant> = {
  PENDING: 'neutral',
  PRE_FLIGHT: 'info',
  READY: 'info',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  FAILED: 'danger',
  ROLLED_BACK: 'danger',
  DEFERRED: 'warning',
  MANUAL_HOLD: 'warning',
};

const BUMP_TONE: Record<BumpType, string> = {
  patch: 'bg-biz-info-subtle text-biz-info border-biz-info/30',
  minor: 'bg-biz-warning-subtle text-biz-warning border-biz-warning/30',
  major: 'bg-biz-danger-subtle text-biz-danger border-biz-danger/30',
};

const CANARY_GATES: number[] = [5, 25, 75, 100];

export function MigrationDetailPage(): JSX.Element {
  const { t } = useTranslation('adminMigrations');
  const params = useParams<{ id: string }>();
  const migrationQuery = useProfileMigration(params.id);
  const runsQuery = useMigrationRuns(params.id);
  const [actionOpen, setActionOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<MigrationAction>('pause');

  if (!params.id) {
    return <Navigate to="/admin/migrations" replace />;
  }

  if (migrationQuery.isLoading) {
    return <LoadingSkeleton />;
  }

  if (migrationQuery.isError || !migrationQuery.data) {
    return (
      <div className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto">
        <div
          role="alert"
          className="px-biz-content-lg py-biz-content-lg rounded-biz-card bg-biz-danger-subtle text-biz-danger border border-biz-danger/30"
        >
          {t('detail.notFound')}
        </div>
      </div>
    );
  }

  const migration = migrationQuery.data;

  const openAction = (action: MigrationAction) => {
    setCurrentAction(action);
    setActionOpen(true);
  };

  const overallPct =
    migration.stats.eligible > 0
      ? Math.round((migration.stats.migrated / migration.stats.eligible) * 100)
      : 0;

  return (
    <div className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto space-y-biz-section">
      <nav className="text-biz-sm" aria-label={t('detail.breadcrumbLabel')}>
        <ol className="flex items-center gap-biz-content text-biz-muted-foreground">
          <li>
            <Link
              to="/admin/migrations"
              className="inline-flex items-center gap-1 hover:text-biz-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring rounded"
            >
              <ArrowLeft size={16} weight="regular" aria-hidden="true" />
              {t('detail.backToList')}
            </Link>
          </li>
          <li aria-hidden="true">
            <CaretRight size={14} weight="regular" />
          </li>
          <li className="text-biz-foreground font-medium truncate">
            {migration.subVerticalDisplayName} · v{migration.fromVersion} → v
            {migration.toVersion}
          </li>
        </ol>
      </nav>

      <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-biz-content-lg">
        <div className="min-w-0">
          <div className="flex items-center gap-biz-content-lg flex-wrap mb-biz-content">
            <h1 className="text-biz-3xl biz-heading font-semibold">
              {migration.subVerticalDisplayName}
            </h1>
            <span
              className={cn(
                'inline-flex items-center px-biz-content py-1 rounded-biz-full border text-biz-xs font-semibold uppercase tracking-wider',
                BUMP_TONE[migration.bumpType],
              )}
            >
              {t(`bumpType.${migration.bumpType}` as const)}
            </span>
            <StatusBadge
              variant={STATE_VARIANT[migration.state]}
              label={t(`state.${migration.state}` as const)}
            />
            {migration.paused ? (
              <StatusBadge variant="warning" label={t('detail.pausedBadge')} />
            ) : null}
          </div>
          <div className="flex items-center gap-biz-content-lg flex-wrap text-biz-sm text-biz-muted-foreground">
            <span className="font-mono">{migration.subVerticalKey}</span>
            <span aria-hidden="true">·</span>
            <span className="font-mono">
              v{migration.fromVersion} → v{migration.toVersion}
            </span>
            <span aria-hidden="true">·</span>
            <span>{t(`migrationClass.${migration.migrationClass}` as const)}</span>
          </div>
        </div>
      </header>

      <ActionPanel migration={migration} onAction={openAction} />

      <section>
        <h2 className="text-biz-lg biz-heading font-semibold mb-biz-content-lg">
          {t('detail.statsTitle')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-biz-content-lg">
          <StatCard tone="neutral" label={t('stats.eligible')} value={migration.stats.eligible} />
          <StatCard tone="success" label={t('stats.migrated')} value={migration.stats.migrated} />
          <StatCard tone="warning" label={t('stats.deferred')} value={migration.stats.deferred} />
          <StatCard tone="danger" label={t('stats.failed')} value={migration.stats.failed} />
          <StatCard tone="paused" label={t('stats.manualHold')} value={migration.stats.manualHold} />
          <StatCard tone="info" label={t('stats.pending')} value={migration.stats.pending} />
        </div>
        <div className="mt-biz-content-lg">
          <ProgressBar
            value={overallPct}
            tone={
              migration.state === 'FAILED' || migration.state === 'ROLLED_BACK'
                ? 'danger'
                : migration.state === 'COMPLETED'
                  ? 'success'
                  : 'primary'
            }
            label={t('detail.overallProgress', {
              migrated: migration.stats.migrated,
              eligible: migration.stats.eligible,
            })}
          />
        </div>
      </section>

      <CanaryTimeline migration={migration} />

      <FailedRunsSection migrationId={migration.id} runs={runsQuery.data ?? []} />

      <BreakingChangesSection migration={migration} />

      <MigrationActionDialog
        open={actionOpen}
        onOpenChange={setActionOpen}
        migration={migration}
        action={currentAction}
      />
    </div>
  );
}

function ActionPanel({
  migration,
  onAction,
}: {
  migration: ProfileMigrationRecord;
  onAction: (a: MigrationAction) => void;
}): JSX.Element {
  const { t } = useTranslation('adminMigrations');
  const canAct =
    migration.state !== 'COMPLETED' && migration.state !== 'ROLLED_BACK';
  const canProceed = canAct && migration.nextGatePercentage !== undefined;
  const canPause = canAct && !migration.paused;
  const canResume = canAct && migration.paused;

  if (!canAct) {
    return (
      <Card>
        <CardBody className="flex items-center gap-biz-content-lg text-biz-muted-foreground">
          <Circle size={18} weight="regular" aria-hidden="true" />
          <span>{t('detail.terminalState')}</span>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div>
          <h2 className="text-biz-lg biz-heading font-semibold">{t('detail.actionsTitle')}</h2>
          <p className="text-biz-sm text-biz-muted-foreground">
            {t('detail.actionsSubtitle')}
          </p>
        </div>
      </CardHeader>
      <CardBody className="flex flex-wrap gap-biz-content-lg">
        {canPause ? (
          <Button
            intent="secondary"
            leftIcon={<PauseCircle size={18} weight="regular" />}
            onClick={() => onAction('pause')}
          >
            {t('detail.ctaPause')}
          </Button>
        ) : null}
        {canResume ? (
          <Button
            intent="secondary"
            leftIcon={<PlayCircle size={18} weight="regular" />}
            onClick={() => onAction('resume')}
          >
            {t('detail.ctaResume')}
          </Button>
        ) : null}
        {canProceed ? (
          <Button
            intent="primary"
            leftIcon={<ArrowsClockwise size={18} weight="regular" />}
            onClick={() => onAction('proceed')}
          >
            {t('detail.ctaProceed', { next: migration.nextGatePercentage })}
          </Button>
        ) : null}
        <Button
          intent="danger"
          leftIcon={<XCircle size={18} weight="regular" />}
          onClick={() => onAction('abort')}
        >
          {t('detail.ctaAbort')}
        </Button>
      </CardBody>
    </Card>
  );
}

function CanaryTimeline({
  migration,
}: {
  migration: ProfileMigrationRecord;
}): JSX.Element {
  const { t } = useTranslation('adminMigrations');
  return (
    <Card>
      <CardHeader>
        <div>
          <h2 className="text-biz-lg biz-heading font-semibold">
            {t('detail.canaryTitle')}
          </h2>
          <p className="text-biz-sm text-biz-muted-foreground">
            {t('detail.canarySubtitle')}
          </p>
        </div>
      </CardHeader>
      <CardBody>
        <ol className="grid grid-cols-2 md:grid-cols-4 gap-biz-content-lg">
          {CANARY_GATES.map((gate) => {
            const state: 'done' | 'current' | 'pending' =
              migration.canaryPercentage >= gate
                ? 'done'
                : migration.nextGatePercentage === gate
                  ? 'current'
                  : 'pending';
            return (
              <li
                key={gate}
                className={cn(
                  'px-biz-content-lg py-biz-content-lg rounded-biz-card border flex items-start gap-biz-content-lg min-h-[96px]',
                  state === 'done' &&
                    'bg-biz-success-subtle border-biz-success/30 text-biz-success',
                  state === 'current' &&
                    'bg-biz-info-subtle border-biz-info/30 text-biz-info',
                  state === 'pending' &&
                    'bg-biz-muted border-biz-border text-biz-muted-foreground',
                )}
              >
                <span aria-hidden="true" className="mt-0.5 shrink-0">
                  {state === 'done' ? (
                    <CheckCircle size={20} weight="regular" />
                  ) : state === 'current' ? (
                    <ArrowsClockwise size={20} weight="regular" />
                  ) : (
                    <Circle size={20} weight="regular" />
                  )}
                </span>
                <div className="min-w-0">
                  <div className="biz-numeric text-biz-xl font-bold">{gate}%</div>
                  <div className="text-biz-xs mt-1">
                    {t(`detail.canaryGateState.${state}` as const)}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </CardBody>
    </Card>
  );
}

function FailedRunsSection({
  migrationId,
  runs,
}: {
  migrationId: string;
  runs: MigrationRunRecord[];
}): JSX.Element | null {
  const { t } = useTranslation('adminMigrations');
  const applyTenantAction = useApplyTenantRunAction(migrationId);
  const failed = runs.filter(
    (r) => r.state === 'FAILED' || r.state === 'DEFERRED' || r.state === 'MANUAL_HOLD',
  );

  if (failed.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div>
            <h2 className="text-biz-lg biz-heading font-semibold">
              {t('detail.failedTitle')}
            </h2>
            <p className="text-biz-sm text-biz-muted-foreground">
              {t('detail.failedSubtitle')}
            </p>
          </div>
        </CardHeader>
        <CardBody className="text-biz-muted-foreground">
          {t('detail.failedEmpty')}
        </CardBody>
      </Card>
    );
  }

  const handleInline = async (
    run: MigrationRunRecord,
    action: 'retry' | 'rollback',
  ) => {
    const reason =
      action === 'retry'
        ? window.prompt(t('detail.inlineRetryPrompt'))
        : window.prompt(t('detail.inlineRollbackPrompt'));
    if (!reason || reason.trim().length < 10) return;
    await applyTenantAction.mutateAsync({
      runId: run.id,
      values: { action, reason },
    });
  };

  return (
    <Card>
      <CardHeader>
        <div>
          <h2 className="text-biz-lg biz-heading font-semibold">
            {t('detail.failedTitle')}
          </h2>
          <p className="text-biz-sm text-biz-muted-foreground">
            {t('detail.failedSubtitle')}
          </p>
        </div>
      </CardHeader>
      <ul className="divide-y divide-biz-border">
        {failed.map((run) => (
          <li
            key={run.id}
            className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-biz-content-lg px-biz-card py-biz-content-lg"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-biz-content flex-wrap">
                <Warning
                  size={18}
                  weight="regular"
                  aria-hidden="true"
                  className="text-biz-warning shrink-0"
                />
                <span className="font-semibold truncate">{run.tenantName}</span>
                <StatusBadge
                  variant={STATE_VARIANT[run.state]}
                  label={t(`state.${run.state}` as const)}
                />
                <span className="text-biz-xs text-biz-muted-foreground font-mono">
                  {run.correlationId}
                </span>
              </div>
              {run.errorMessage ? (
                <p className="mt-biz-content text-biz-sm text-biz-danger font-mono break-words">
                  {run.errorMessage}
                </p>
              ) : null}
              <div className="mt-biz-content text-biz-xs text-biz-muted-foreground">
                {t('detail.attempts', { count: run.attempts })}
                {run.lastAttemptAt
                  ? ` · ${new Date(run.lastAttemptAt).toLocaleString()}`
                  : ''}
              </div>
            </div>
            <div className="flex items-start gap-biz-content-lg md:self-center">
              {run.state === 'FAILED' || run.state === 'DEFERRED' ? (
                <Button
                  intent="secondary"
                  size="sm"
                  leftIcon={<ArrowsClockwise size={16} weight="regular" />}
                  onClick={() => handleInline(run, 'retry')}
                  loading={applyTenantAction.isPending}
                >
                  {t('detail.inlineRetry')}
                </Button>
              ) : null}
              <Button
                intent="ghost"
                size="sm"
                leftIcon={<XCircle size={16} weight="regular" />}
                onClick={() => handleInline(run, 'rollback')}
                loading={applyTenantAction.isPending}
              >
                {t('detail.inlineRollback')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function BreakingChangesSection({
  migration,
}: {
  migration: ProfileMigrationRecord;
}): JSX.Element {
  const { t } = useTranslation('adminMigrations');
  if (migration.breakingChanges.length === 0) {
    return (
      <Card>
        <CardHeader>
          <h2 className="text-biz-lg biz-heading font-semibold">
            {t('detail.breakingTitle')}
          </h2>
        </CardHeader>
        <CardBody className="text-biz-muted-foreground">
          {t('detail.breakingEmpty')}
        </CardBody>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader>
        <div>
          <h2 className="text-biz-lg biz-heading font-semibold">
            {t('detail.breakingTitle')}
          </h2>
          <p className="text-biz-sm text-biz-muted-foreground">
            {t('detail.breakingSubtitle')}
          </p>
        </div>
      </CardHeader>
      <ul className="divide-y divide-biz-border">
        {migration.breakingChanges.map((c, i) => (
          <li key={i} className="px-biz-card py-biz-content-lg">
            <div className="font-mono text-biz-sm font-semibold">{c.field}</div>
            <div className="text-biz-sm text-biz-muted-foreground mt-1">{c.change}</div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <div
      className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="h-8 w-1/3 bg-biz-muted rounded animate-pulse mb-biz-content" />
      <div className="h-4 w-1/5 bg-biz-muted rounded animate-pulse mb-biz-section" />
      <div className="h-40 bg-biz-muted rounded-biz-card animate-pulse" />
    </div>
  );
}
